import React from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { UserStatus } from '@atlassian/ptc-common';
import { CompactEmptyState } from '@atlassian/team-work';

import PlacesListEmptyImage from '../../assets/empty-places-list-state/PlacesListEmpty_ptc.svg';

import { type EmptyPlacesListStateProps } from './types';

const EmptyPlacesListState = ({
	isCurrentUser,
	userFullName,
	userStatus,
}: EmptyPlacesListStateProps) => {
	const intl = useIntl();

	const renderDescText = () => {
		const descMessage = isCurrentUser
			? messages.noPlacesListOwnerDesc
			: userStatus === UserStatus.inactive || userStatus === UserStatus.closed
				? messages.noPlacesListPastUserDesc
				: messages.noPlacesListOtherDesc;

		return <FormattedMessage {...descMessage} values={{ user: userFullName }} />;
	};

	return (
		<CompactEmptyState
			title={intl.formatMessage(messages.noPlacesListTitle)}
			description={renderDescText()}
			imageUrl={PlacesListEmptyImage}
		/>
	);
};

const messages = defineMessages({
	noPlacesListTitle: {
		id: 'ptc-directory.profile-page.places-list.no-places-list.title',
		defaultMessage: 'We don’t have places to show here yet',
		description:
			'Title of the message displayed when there is no places associated to the profile that is being viewed',
	},
	noPlacesListOwnerDesc: {
		id: 'ptc-directory.profile-page.places-list.no-places-list.owner.description',
		defaultMessage: 'There are no projects or spaces you’ve worked in across the last 90 days.',
		description:
			'Description of the message displayed when there is no places associated to the owner profile that is being viewed',
	},
	noPlacesListOtherDesc: {
		id: 'ptc-directory.profile-page.places-list.no-places-list.other.description',
		defaultMessage: '{user} hasn’t worked in any projects or spaces in the last 90 days.',
		description:
			'Description of the message displayed when there is no places associated to the person profile that is being viewed',
	},
	noPlacesListPastUserDesc: {
		id: 'ptc-directory.profile-page.places-list.no-places-list.past-user.description',
		defaultMessage:
			'{user}’s account is deactivated or deleted. We can’t show the projects or spaces {user} worked in across the last 90 days.',
		description:
			'Description of the message displayed when there is no places associated to a deactivated or deleted users profile that is being viewed',
	},
});

export default EmptyPlacesListState;
